import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clampStorageBoxSidebarWidth } from '../utils/storageBoxSidebarWidth';

const DESKTOP_MQ = '(min-width: 961px)';

export default function StorageBoxShellSidebarResizeHandle({ widthPx, onWidthChange, onCommit }) {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_MQ).matches : false
  );
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startWidth: widthPx });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(DESKTOP_MQ);
    const sync = () => setDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!dragging) return undefined;
    document.body.classList.add('sb-shell-sidebar-resize-active');
    return () => {
      document.body.classList.remove('sb-shell-sidebar-resize-active');
    };
  }, [dragging]);

  useEffect(() => {
    if (!dragging) return undefined;

    const onMove = (e) => {
      const { startX, startWidth } = dragRef.current;
      const next = clampStorageBoxSidebarWidth(startWidth + (e.clientX - startX));
      onWidthChange(next);
    };

    const onUp = () => {
      setDragging(false);
      onCommit?.();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, onWidthChange, onCommit]);

  const onPointerDown = useCallback(
    (e) => {
      if (!desktop || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = { startX: e.clientX, startWidth: widthPx };
      setDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [desktop, widthPx]
  );

  if (!desktop) return null;

  return (
    <div
      className={`sb-shell-sidebar-resize-handle${dragging ? ' is-dragging' : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      title="Drag to resize sidebar"
      onPointerDown={onPointerDown}
    />
  );
}
